// client/src/components/LessonEdit.js
// Lesson creation and editing component for superadmins

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HeadlineMedium, LabelMedium } from './Typography';
import { db, storage } from '../firebase';
import { doc, getDoc, setDoc, addDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Toast from './Toast';

const LessonEdit = ({ onBackToLessons }) => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [courses, setCourses] = useState([]);
  
  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    course_id: '',
    level: 1,
    lesson_number: 1,
    lesson_type: 'text',
    video_link: '',
    graphic_link: '',
    excerpt: '',
    description: '',
    headline: ''
  });

  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  
  // Image upload state
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Toast helper functions
  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  // Image upload function
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please select a valid image file', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image size must be less than 5MB', 'error');
      return;
    }

    try {
      setUploading(true);
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `lessons/${timestamp}_${file.name}`;
      
      // Create storage reference
      const storageRef = ref(storage, fileName);
      
      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update form data
      handleInputChange('graphic_link', downloadURL);
      
      // Set preview
      setImagePreview(downloadURL);
      
      showToast('Image uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast('Failed to upload image', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Load courses for dropdown
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const coursesRef = collection(db, 'courses');
        const coursesSnapshot = await getDocs(coursesRef);
        const coursesData = [];
        coursesSnapshot.forEach(doc => {
          coursesData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        coursesData.sort((a, b) => (a.course_name || a.title || '').localeCompare(b.course_name || b.title || ''));
        setCourses(coursesData);
      } catch (error) {
        console.error('Error loading courses:', error);
        showToast('Failed to load courses', 'error');
      }
    };

    loadCourses();
  }, []);

  // Load lesson data
  useEffect(() => {
    const loadLessonData = async () => {
      if (!lessonId) {
        // Creating new lesson - set default values
        setFormData({
          title: '',
          course_id: '',
          level: 1,
          lesson_number: 1,
          lesson_type: 'text',
          video_link: '',
          graphic_link: '',
          excerpt: '',
          description: '',
          headline: ''
        });
        setTasks([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);

        // Fetch existing lesson details
        const lessonRef = doc(db, 'lessons', lessonId);
        const lessonSnapshot = await getDoc(lessonRef);
        
        if (lessonSnapshot.exists()) {
          const lessonData = lessonSnapshot.data();
          setFormData({
            title: lessonData.title || '',
            course_id: lessonData.course_id || '',
            level: lessonData.level || 1,
            lesson_number: lessonData.lesson_number || 1,
            lesson_type: lessonData.lesson_type || 'text',
            video_link: lessonData.video_link || '',
            graphic_link: lessonData.graphic_link || '',
            excerpt: lessonData.excerpt || '',
            description: lessonData.description || '',
            headline: lessonData.headline || ''
          });
          setTasks(lessonData.todos || []);
          
          // Set image preview if it's a graphic lesson
          if (lessonData.lesson_type === 'graphic' && lessonData.graphic_link) {
            setImagePreview(lessonData.graphic_link);
          }
        } else {
          showToast('Lesson not found', 'error');
        }

      } catch (error) {
        console.error('Error loading lesson data:', error);
        showToast('Failed to load lesson data', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadLessonData();
  }, [lessonId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Rich text editor functions
  const applyFormat = (field, format) => {
    const textarea = document.getElementById(`lesson-${field}`);
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    let formattedText = '';
    let cursorOffset = 0;
    
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        cursorOffset = formattedText.length;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        cursorOffset = formattedText.length;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        cursorOffset = formattedText.length;
        break;
      case 'headline':
        formattedText = `### ${selectedText}`;
        cursorOffset = formattedText.length;
        break;
      case 'bullet':
        formattedText = `- ${selectedText}`;
        cursorOffset = formattedText.length;
        break;
      case 'numbered':
        formattedText = `1. ${selectedText}`;
        cursorOffset = formattedText.length;
        break;
      case 'link':
        const url = prompt('Enter URL:');
        if (url) {
          formattedText = `[${selectedText}](${url})`;
          cursorOffset = formattedText.length;
        } else {
          formattedText = selectedText;
          cursorOffset = selectedText.length;
        }
        break;
      case 'quote':
        formattedText = `> ${selectedText}`;
        cursorOffset = formattedText.length;
        break;
      case 'horizontal':
        formattedText = selectedText + '\n---\n';
        cursorOffset = formattedText.length;
        break;
      case 'linebreak':
        formattedText = selectedText + '\n';
        cursorOffset = formattedText.length;
        break;
      default:
        formattedText = selectedText;
        cursorOffset = formattedText.length;
    }
    
    const newValue = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end);
    handleInputChange(field, newValue);
    
    // Set cursor position after formatting
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + cursorOffset, start + cursorOffset);
    }, 0);
  };

  // Task management functions
  const addTask = () => {
    if (newTask.trim()) {
      setTasks(prev => [...prev, newTask.trim()]);
      setNewTask('');
    }
  };

  const removeTask = (index) => {
    setTasks(prev => prev.filter((_, i) => i !== index));
  };

  const updateTask = (index, value) => {
    setTasks(prev => prev.map((task, i) => i === index ? value : task));
  };

  const moveTask = (fromIndex, toIndex) => {
    setTasks(prev => {
      const newTasks = [...prev];
      const [movedTask] = newTasks.splice(fromIndex, 1);
      newTasks.splice(toIndex, 0, movedTask);
      return newTasks;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Prepare lesson data
      const lessonData = {
        title: formData.title.trim(),
        course_id: formData.course_id,
        level: parseInt(formData.level) || 1,
        lesson_number: parseInt(formData.lesson_number) || 1,
        lesson_type: formData.lesson_type,
        video_link: formData.video_link.trim(),
        graphic_link: formData.graphic_link.trim(),
        excerpt: formData.excerpt.trim(),
        description: formData.description.trim(),
        headline: formData.headline.trim(),
        todos: tasks,
        updatedAt: new Date().toISOString()
      };

      if (lessonId) {
        // Update existing lesson
        const lessonRef = doc(db, 'lessons', lessonId);
        await setDoc(lessonRef, lessonData, { merge: true });
        showToast('Lesson updated successfully!', 'success');
      } else {
        // Create new lesson
        lessonData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'lessons'), lessonData);
        showToast('Lesson created successfully!', 'success');
      }

    } catch (error) {
      console.error('Error saving lesson:', error);
      showToast('Failed to save lesson', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete lesson function
  const handleDeleteLesson = async () => {
    if (!lessonId) {
      showToast('Cannot delete a lesson that has not been saved yet', 'error');
      return;
    }

    try {
      setDeleting(true);
      
      // Delete the lesson from Firestore
      const lessonRef = doc(db, 'lessons', lessonId);
      await deleteDoc(lessonRef);
      
      showToast('Lesson deleted successfully!', 'success');
      
      // Navigate back to lessons list after a short delay
      setTimeout(() => {
        if (onBackToLessons) {
          onBackToLessons();
        } else {
          navigate('/admin/lessons');
        }
      }, 1500);
      
    } catch (error) {
      console.error('Error deleting lesson:', error);
      showToast('Failed to delete lesson', 'error');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading lesson...</span>
      </div>
    );
  }

  const pageTitle = lessonId ? 'Lesson Edit' : 'Create New Lesson';
  const saveButtonText = saving ? 'Saving...' : (lessonId ? 'Save Changes' : 'Create Lesson');

  return (
    <div className="space-y-8">
      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={hideToast}
        duration={3000}
      />

      {/* Back Button */}
      {onBackToLessons && (
        <div className="mb-4">
          <button
            onClick={onBackToLessons}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Lessons
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <HeadlineMedium color="gray-900">{pageTitle}</HeadlineMedium>
        <div className="flex items-center space-x-3">
          {lessonId && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              Delete Lesson
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saveButtonText}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('content')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'content'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Lesson Content
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tasks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Lesson Tasks
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'content' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Lesson Title */}
            <div className="md:col-span-2">
              <LabelMedium color="gray-700">Lesson Title</LabelMedium>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter lesson title"
              />
            </div>

            {/* Course Selection */}
            <div>
              <LabelMedium color="gray-700">Course</LabelMedium>
              <select
                value={formData.course_id}
                onChange={(e) => handleInputChange('course_id', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a course</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.course_name || course.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Level */}
            <div>
              <LabelMedium color="gray-700">Level</LabelMedium>
              <select
                value={formData.level}
                onChange={(e) => handleInputChange('level', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[1, 2, 3, 4, 5, 6].map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            {/* Lesson Order */}
            <div>
              <LabelMedium color="gray-700">Lesson Order</LabelMedium>
              <input
                type="number"
                value={formData.lesson_number}
                onChange={(e) => handleInputChange('lesson_number', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1"
                min="1"
              />
            </div>

            {/* Lesson Type */}
            <div>
              <LabelMedium color="gray-700">Lesson Type</LabelMedium>
              <select
                value={formData.lesson_type}
                onChange={(e) => handleInputChange('lesson_type', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="text">Text</option>
                <option value="video">Video</option>
                <option value="graphic">Graphic</option>
                <option value="excerpt">Excerpt</option>
              </select>
            </div>

            {/* Conditional Fields Based on Lesson Type */}
            {formData.lesson_type === 'video' && (
              <div className="md:col-span-2">
                <LabelMedium color="gray-700">Video Link</LabelMedium>
                <input
                  type="text"
                  value={formData.video_link}
                  onChange={(e) => handleInputChange('video_link', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter video URL"
                />
              </div>
            )}

            {formData.lesson_type === 'graphic' && (
              <div className="md:col-span-2">
                <LabelMedium color="gray-700">Graphic Image</LabelMedium>
                
                {/* Image Upload Section */}
                <div className="mt-1 space-y-4">
                  {/* Upload Button */}
                  <div className="flex items-center space-x-4">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                      <div className={`px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors ${
                        uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}>
                        {uploading ? 'Uploading...' : 'Choose Image'}
                      </div>
                    </label>
                    
                    {uploading && (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-gray-600">Uploading...</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="space-y-2">
                      <div className="relative inline-block">
                        <img
                          src={imagePreview}
                          alt="Graphic preview"
                          className="max-w-xs max-h-48 rounded-lg border border-gray-300 object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            handleInputChange('graphic_link', '');
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Manual URL Input (Alternative) */}
                  <div className="border-t pt-4">
                    <LabelMedium color="gray-600" className="text-sm">Or enter image URL manually:</LabelMedium>
                    <input
                      type="text"
                      value={formData.graphic_link}
                      onChange={(e) => {
                        handleInputChange('graphic_link', e.target.value);
                        setImagePreview(e.target.value);
                      }}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.lesson_type === 'excerpt' && (
              <div className="md:col-span-2">
                <LabelMedium color="gray-700">Excerpt</LabelMedium>
                <div className="mt-1">
                  {/* Rich Text Toolbar */}
                  <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 border border-gray-300 rounded-t-md">
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'bold')}
                      className="px-3 py-1 text-sm font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Bold"
                    >
                      B
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'italic')}
                      className="px-3 py-1 text-sm italic bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Italic"
                    >
                      I
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'underline')}
                      className="px-3 py-1 text-sm underline bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Underline"
                    >
                      U
                    </button>
                    <div className="w-px h-6 bg-gray-300"></div>
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'headline')}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Headline (H4)"
                    >
                      H4
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'bullet')}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Bullet Point"
                    >
                      •
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'numbered')}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Numbered List"
                    >
                      1.
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'quote')}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Quote"
                    >
                      "
                    </button>
                    <div className="w-px h-6 bg-gray-300"></div>
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'link')}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Hyperlink"
                    >
                      🔗
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'horizontal')}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Horizontal Line"
                    >
                      ─
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('excerpt', 'linebreak')}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Line Break"
                    >
                      ↵
                    </button>
                  </div>
                  
                  {/* Text Area */}
                  <textarea
                    id="lesson-excerpt"
                    value={formData.excerpt}
                    onChange={(e) => handleInputChange('excerpt', e.target.value)}
                    rows={4}
                    className="block w-full border border-gray-300 rounded-b-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter lesson excerpt (use B/I/U/↵ buttons above to format selected text)"
                  />
                </div>
              </div>
            )}

            {/* Lesson Headline */}
            <div className="md:col-span-2">
              <LabelMedium color="gray-700">Lesson Headline</LabelMedium>
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => handleInputChange('headline', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter lesson headline"
              />
            </div>

            {/* Lesson Description */}
            <div className="md:col-span-2">
              <LabelMedium color="gray-700">Lesson Description</LabelMedium>
              <div className="mt-1">
                {/* Rich Text Toolbar */}
                <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 border border-gray-300 rounded-t-md">
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'bold')}
                    className="px-3 py-1 text-sm font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Bold"
                  >
                    B
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'italic')}
                    className="px-3 py-1 text-sm italic bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Italic"
                  >
                    I
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'underline')}
                    className="px-3 py-1 text-sm underline bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Underline"
                  >
                    U
                  </button>
                  <div className="w-px h-6 bg-gray-300"></div>
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'headline')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Headline (H4)"
                  >
                    H4
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'bullet')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Bullet Point"
                  >
                    •
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'numbered')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Numbered List"
                  >
                    1.
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'quote')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Quote"
                  >
                    "
                  </button>
                  <div className="w-px h-6 bg-gray-300"></div>
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'link')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Hyperlink"
                  >
                    🔗
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'horizontal')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Horizontal Line"
                  >
                    ─
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('description', 'linebreak')}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Line Break"
                  >
                    ↵
                  </button>
                </div>
                
                {/* Text Area */}
                <textarea
                  id="lesson-description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={6}
                  className="block w-full border border-gray-300 rounded-b-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter lesson description (use B/I/U/↵ buttons above to format selected text)"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="space-y-4">
            {/* Existing Tasks */}
            {tasks.map((task, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <input
                    type="text"
                    value={task}
                    onChange={(e) => updateTask(index, e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter task description"
                  />
                </div>
                
                {/* Reorder Buttons */}
                <div className="flex space-x-1">
                  {index > 0 && (
                    <button
                      onClick={() => moveTask(index, index - 1)}
                      className="p-2 text-gray-500 hover:text-gray-700"
                      title="Move up"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                  )}
                  {index < tasks.length - 1 && (
                    <button
                      onClick={() => moveTask(index, index + 1)}
                      className="p-2 text-gray-500 hover:text-gray-700"
                      title="Move down"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Delete Button */}
                <button
                  onClick={() => removeTask(index)}
                  className="p-2 text-red-500 hover:text-red-700"
                  title="Delete task"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
            
            {/* Add New Task */}
            <div className="flex space-x-3 p-3 bg-blue-50 rounded-lg">
              <input
                type="text"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTask()}
                className="flex-1 border border-blue-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add task"
              />
              <button
                onClick={addTask}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Lesson</h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this lesson? This action cannot be undone.
                {formData.title && (
                  <span className="block mt-2 font-medium text-gray-900">
                    "{formData.title}"
                  </span>
                )}
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteLesson}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 flex items-center"
              >
                {deleting && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {deleting ? 'Deleting...' : 'Delete Lesson'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonEdit;
